/**
 * Help Panel Component
 *
 * Displays contextual help information in a side panel.
 * Supports collapsible sections, sticky scroll behavior, and responsive mobile drawer.
 */

import React, { useState, type ReactNode } from 'react';
import { X, ChevronUp, ChevronDown, HelpCircle } from 'lucide-react';

export interface HelpPanelProps {
  title?: string;
  children: ReactNode;
  defaultCollapsed?: boolean;
  sticky?: boolean;
  className?: string;
  position?: 'right' | 'left';
  mobilePosition?: 'bottom' | 'modal';
}

export const HelpPanel: React.FC<HelpPanelProps> = ({
  title = 'Help & Resources',
  children,
  defaultCollapsed = false,
  sticky = true,
  className = '',
  position = 'right',
  mobilePosition = 'bottom',
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const positionClasses = {
    right: 'lg:ml-6',
    left: 'lg:mr-6',
  };

  const stickyClass = sticky ? 'lg:sticky lg:top-6 lg:self-start' : '';

  return (
    <>
      {/* Mobile FAB Button */}
      <button
        onClick={() => setIsMobileOpen(true)}
        className="lg:hidden fixed bottom-6 right-6 z-40 w-14 h-14 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all"
        aria-label="Open help panel"
      >
        <HelpCircle className="w-6 h-6" />
      </button>

      {/* Desktop Help Panel */}
      <aside
        className={`hidden lg:block w-80 shrink-0 ${positionClasses[position]} ${stickyClass} ${className}`}
      >
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          {/* Header */}
          <div
            className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            <div className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-primary-600" />
              <h3 className="font-semibold text-gray-900">{title}</h3>
            </div>
            <button
              type="button"
              className="p-1 hover:bg-gray-200 rounded transition-colors"
              aria-label={isCollapsed ? 'Expand panel' : 'Collapse panel'}
            >
              {isCollapsed ? (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              )}
            </button>
          </div>

          {/* Content */}
          {!isCollapsed && (
            <div className="p-4 space-y-4 max-h-[calc(100vh-12rem)] overflow-y-auto">
              {children}
            </div>
          )}
        </div>
      </aside>

      {/* Mobile Help Panel (Bottom Drawer or Modal) */}
      {isMobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className="lg:hidden fixed inset-0 bg-black/50 z-50"
            onClick={() => setIsMobileOpen(false)}
            aria-hidden="true"
          />

          {/* Drawer/Modal */}
          <div
            className={`lg:hidden fixed z-50 bg-white shadow-xl ${
              mobilePosition === 'bottom'
                ? 'bottom-0 left-0 right-0 rounded-t-2xl max-h-[80vh]'
                : 'inset-4 rounded-2xl'
            } overflow-hidden animate-slideUp`}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-primary-600" />
                <h3 className="font-semibold text-gray-900">{title}</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsMobileOpen(false)}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                aria-label="Close help panel"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(80vh-4rem)]">
              {children}
            </div>
          </div>
        </>
      )}
    </>
  );
};
